<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="d-flex align-center">
        <v-toolbar flat class="w-100">
          <v-toolbar-title>{{ $t('courses') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <course-form 
          v-if="getUserRole() === 'Admin'"
          @courseAdded="fetchCourses"></course-form>
        </v-toolbar>
      </v-col>
      
      <!-- Job Title Filter -->
      <!-- <v-col cols="12" sm="4">
        <v-select
          v-model="selectedJobTitle"
          :items="jobTitles"
          :label="$t('filterByJobTitle')"
          clearable
          @change="filterCourses"
        ></v-select>
      </v-col> -->
    </v-row>

    <!-- Loading State -->
    <v-row v-if="loading">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>

    <!-- No Data State -->
    <v-row v-else-if="filteredCourses.length === 0">
      <v-col cols="12" class="text-center">
        <v-alert type="info">
          {{ selectedJobTitle ? $t('noCoursesFoundForJobTitle') : $t('noCoursesFound') }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- Card View -->
    

    <v-row>
      <v-col v-for="course in filteredCourses" 
             :key="course.courseId" 
             cols="12" sm="6" md="4" lg="3">
             <v-card 
  class="h-100 position-relative cursor-pointer" 
  @click.stop="navigateToCourseDetails(course)"
>
          
          <!-- Existing card content -->
          <v-img
            :src="course.thumbnail ? `${config.hostname}/${course.thumbnail}` : '/default-course-thumbnail.jpg'"
            height="200"
            class="grey lighten-2"
          >
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-icon size="64">mdi-book-open-page-variant</v-icon>
              </v-row>
            </template>
          </v-img>

          <v-card-title class="text-truncate">{{ course.title }}</v-card-title>

          <v-card-subtitle>
            <!-- <v-chip small color="primary" class="mr-2">
              {{ course.jobTitle }}
            </v-chip> -->
            <v-chip small>
              {{ course.category }}
            </v-chip>
          </v-card-subtitle>

          <v-card-text class="text-truncate">
            {{ course.description }}
          </v-card-text>

          <v-divider></v-divider>

          <!-- Comment Icon and Actions -->
          <v-card-actions class="d-flex justify-space-between">
            <!-- Existing admin actions -->
            <div v-if="getUserRole() === 'Admin'">
              <v-btn icon small @click.stop="editCourse(course)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon small @click.stop="openDeleteDialog(course)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>

            <!-- Comment Button -->
            <v-btn 
              icon 
              @click.stop="openComments(course)"
            >
              <v-badge 
                :content="course.commentCount || 0" 
                :value="course.commentCount"
                color="primary"
              >
                <v-icon>mdi-comment-outline</v-icon>
              </v-badge>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Comments Dialog -->
    <v-dialog 
      v-model="commentDialog" 
      max-width="600" 
      persistent
    >
      <v-card v-if="selectedCourse">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Comments for {{ selectedCourse.title }}</span>
          <v-btn icon @click="closeComments">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Existing Comments List -->
          <v-list v-if="comments.length" dense>
            <v-list-item 
              v-for="comment in comments" 
              :key="comment.id"
              class="mb-2"
            >
            <v-list-item-avatar>
              <v-img
                v-if="comment.authorAvatar"
                :src="`${config.hostname}${comment.authorAvatar}`"
                alt="Avatar"
              ></v-img>
              <v-icon v-else>
                mdi-account-circle
              </v-icon>
            </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ comment.author }}
                </v-list-item-title>
                <div class="comment-text mt-1 mb-1">
                  {{ comment.textContent }}
                </div>
                <v-list-item-subtitle class="text-caption grey--text">
                  {{ formatDate(comment.dateAdded) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              
              <v-list-item-action>
                <v-btn 
                  icon 
                  small 
                  @click.stop="openDeleteCommentDialog(comment)"
                  v-if="canDeleteComment(comment)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-alert 
            v-else 
            type="info" 
            outlined 
            class="mt-3"
          >
            No comments yet. Be the first to comment!
          </v-alert>

          <!-- Add Comment Section -->
          <v-form @submit.prevent="submitComment" class="mt-4">
            <v-text-field
              v-model="newComment"
              label="Add a comment"
              outlined
              dense
              append-icon="mdi-send"
              @click:append="submitComment"
            ></v-text-field>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Comment Confirmation Dialog -->
    <v-dialog v-model="deleteCommentDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ $t('confirmDeleteComment') }}
        </v-card-title>
        <v-card-text>
          {{ $t('deleteCommentConfirmationMessage') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDeleteCommentDialog">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn 
            color="error" 
            text 
            @click="confirmDeleteComment"
          >
            {{ $t('delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Course Form -->
    <edit-course-form
      v-model="editDialog"
      :course="selectedCourse"
      @courseUpdated="handleCourseUpdated"
      @error="handleError"
      @success="handleSuccess"
    ></edit-course-form>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ $t('confirmDelete') }}
        </v-card-title>
        <v-card-text>
          {{ $t('deleteConfirmationMessage', { title: courseToDelete?.title }) }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDeleteDialog">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn 
            color="error" 
            text 
            @click="confirmDelete"
            :loading="deletingCourse"
          >
            {{ $t('delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import CourseForm from './CourseForm.vue';
import EditCourseForm from './EditForm.vue';
import config from '../../../public/config';

export default {
  name: 'CourseList',
  components: {
    CourseForm,
    EditCourseForm
  },
  data() {
    return {
      config,
      courses: [],
      loading: false,
      selectedJobTitle: null,
      jobTitles: [
        'Software Engineer',
        'Data Scientist',
        'Product Manager',
        'UX Designer',
        'DevOps Engineer',
        'QA Engineer',
        'Technical Writer',
        'Scrum Master',
        'Business Analyst',
        'Systems Analyst',
      ],
      deleteDialog: false,
      courseToDelete: null,
      deletingCourse: false,
      snackbar: {
        show: false,
        text: '',
        color: 'success'
      },
      editDialog: false,
      selectedCourse: null,
      commentDialog: false,
      comments: [],
      newComment: '',
      deleteCommentDialog: false,
      commentToDelete: null,
    };
  },
  computed: {
    filteredCourses() {
      if (!this.selectedJobTitle) return this.courses;
      return this.courses.filter(course => course.jobTitle === this.selectedJobTitle);
    }
  },
  created() {
    this.fetchCourses();
  },
  methods: {
    canDeleteComment(comment) {
      const userRole = this.getUserRole();
      const userId = parseInt(localStorage.getItem('loggedUserId'));
      return userRole === 'Admin' || comment.addedBy === userId;
    },

    openDeleteCommentDialog(comment) {
      this.commentToDelete = comment;
      this.deleteCommentDialog = true;
    },

    closeDeleteCommentDialog() {
      this.deleteCommentDialog = false;
      this.commentToDelete = null;
    },

    confirmDeleteComment() {
      if (!this.commentToDelete) return;

      this.$store
        .dispatch('course/deleteComment', { courseId: this.selectedCourse.courseId, 
          commentId: this.commentToDelete.commentId })
        .then(() => {
          this.fetchComments(this.selectedCourse.courseId);
          this.showSnackbar(this.$t('commentDeletedSuccess'), 'success');
        })
        .catch((error) => {
          console.error('Error deleting comment:', error);
          this.showSnackbar(this.$t('errorDeletingComment'), 'error');
        })
        .finally(() => {
          this.closeDeleteCommentDialog();
        });
    },
    fetchCourses() {
      this.loading = true;
      this.$store
        .dispatch('course/fetchCourses')
        .then((response) => {
          this.courses = response.data;
        })
        .catch((error) => {
          console.error('Error fetching courses:', error);
          this.showSnackbar(this.$t('errorFetchingCourses'), 'error');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getUserRole() {
      return this.$store.getters['dashboard/userInfo'].roleName;
    },
    // editCourse(course) {
    //   this.$router.push({ 
    //     name: 'EditCourse', 
    //     params: { courseId: course.courseId } 
    //   });
    // },
    openDeleteDialog(course) {
      this.courseToDelete = course;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.courseToDelete = null;
    },
    confirmDelete() {
      if (!this.courseToDelete) return;
      
      this.deletingCourse = true;
      this.$store
        .dispatch('course/deleteCourse', this.courseToDelete.courseId)
        .then(() => {
          this.showSnackbar(this.$t('courseDeletedSuccess'), 'success');
          this.fetchCourses(); // Refresh the list
        })
        .catch((error) => {
          console.error('Error deleting course:', error);
          this.showSnackbar(this.$t('errorDeletingCourse'), 'error');
        })
        .finally(() => {
          this.deletingCourse = false;
          this.closeDeleteDialog();
        });
    },
    showSnackbar(text, color = 'success') {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    // navigateToCourseDetails(event, { item }) {
    //   try {
    //     this.$router.push({ 
    //       name: 'CourseDetails', 
    //       params: { courseId: item.courseId } 
    //     });
    //   } catch (error) {
    //     console.error('Navigation failed:', error);
    //   }
    // },
    navigateToCourseDetails(course) {
      this.$router.push({ 
        name: 'CourseDetails', 
        params: { courseId: course.courseId } 
      });
    },
    filterCourses() {
     
    },
    editCourse(course) {
      this.selectedCourse = { ...course };
      this.editDialog = true;
    },

    handleCourseUpdated() {
      this.fetchCourses();
    },

    handleError(message) {
      this.showSnackbar(message, 'error');
    },

    handleSuccess(message) {
      this.showSnackbar(message, 'success');
    },
    openComments(course) {
      this.selectedCourse = course;
      this.commentDialog = true;
      this.fetchComments(course.courseId);
    },
    closeComments() {
      this.commentDialog = false;
      this.selectedCourse = null;
      this.comments = [];
      this.newComment = '';
    },
    fetchComments(courseId) {
      this.loading = true;
      this.$store
        .dispatch('course/fetchComments', courseId)
        .then((response) => {
          this.comments = response.data;
        })
        .catch((error) => {
          console.error('Error fetching comments:', error);
          this.showSnackbar(this.$t('errorFetchingComments'), 'error');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitComment() {
      if (!this.newComment.trim()) return;

      this.$store
        .dispatch('course/addComment', {
          courseId: this.selectedCourse.courseId,
          textContent: this.newComment,
          addedBy: parseInt(localStorage.getItem('loggedUserId')),
        })
        .then(() => {
          this.fetchComments(this.selectedCourse.courseId);
          this.newComment = '';
          this.showSnackbar(this.$t('commentAddedSuccess'), 'success');
        })
        .catch((error) => {
          console.error('Error adding comment:', error);
          this.showSnackbar(this.$t('errorAddingComment'), 'error');
        });
    },
    deleteComment(commentId) {
      this.$store
        .dispatch('course/deleteComment', commentId)
        .then(() => {
          this.fetchComments(this.selectedCourse.courseId);
          this.showSnackbar(this.$t('commentDeletedSuccess'), 'success');
        })
        .catch((error) => {
          console.error('Error deleting comment:', error);
          this.showSnackbar(this.$t('errorDeletingComment'), 'error');
        });
    },
    formatDate(date) {
      return new Date(date).toLocaleString();
    }
  }
};
</script>

<style scoped>
.comment-text {
  white-space: normal;
  word-break: break-word;
  max-width: 100%;
  line-height: 1.4;
}
</style>